import { EpisodeWord } from "../../../Episode/services/episode.service";

export type TimeRange = [number, number];
export type ClipEdge = "start" | "end";

export const MIN_CLIP_LENGTH = 0.5;
export const MIN_VIEW_SPAN = 4;

export const SPEAKER_COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ec4899",
  "#0ea5e9",
  "#8b5cf6",
];

export const speakerColor = (speaker?: number): string =>
  SPEAKER_COLORS[(speaker ?? 0) % SPEAKER_COLORS.length];

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const formatTimecode = (seconds: number, withTenths = true): string => {
  const totalTenths = Math.round(Math.max(0, seconds) * 10);
  const tenths = totalTenths % 10;
  const totalSeconds = Math.floor(totalTenths / 10);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = (totalSeconds % 60).toString().padStart(2, "0");
  const base =
    h > 0 ? `${h}:${m.toString().padStart(2, "0")}:${s}` : `${m}:${s}`;
  return withTenths ? `${base}.${tenths}` : base;
};

export const parseTimecode = (value: string): number | null => {
  const parts = value.trim().split(":");
  if (parts.length > 3 || parts.some((part) => part.trim() === "")) {
    return null;
  }
  const numbers = parts.map(Number);
  if (numbers.some((n) => !Number.isFinite(n) || n < 0)) {
    return null;
  }
  return numbers.reduce((total, n) => total * 60 + n, 0);
};

// Index of the last word that starts at or before `time`, or -1.
export const findWordIndexAt = (words: EpisodeWord[], time: number): number => {
  let lo = 0;
  let hi = words.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (words[mid].start <= time) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
};

export const snapToWordBoundary = (
  words: EpisodeWord[],
  time: number,
  edge: ClipEdge,
  tolerance: number,
): number => {
  if (!words.length) return time;
  const index = findWordIndexAt(words, time);
  let best = time;
  let bestDistance = tolerance;
  [index - 1, index, index + 1]
    .filter((i) => i >= 0 && i < words.length)
    .forEach((i) => {
      const boundary = edge === "start" ? words[i].start : words[i].end;
      const distance = Math.abs(boundary - time);
      if (distance <= bestDistance) {
        best = boundary;
        bestDistance = distance;
      }
    });
  return best;
};

export const stepWordBoundary = (
  words: EpisodeWord[],
  time: number,
  edge: ClipEdge,
  direction: -1 | 1,
): number | null => {
  const boundaryOf = (w: EpisodeWord) => (edge === "start" ? w.start : w.end);
  const EPSILON = 0.01;
  if (direction === 1) {
    const next = words.find((w) => boundaryOf(w) > time + EPSILON);
    return next ? boundaryOf(next) : null;
  }
  for (let i = words.length - 1; i >= 0; i -= 1) {
    if (boundaryOf(words[i]) < time - EPSILON) return boundaryOf(words[i]);
  }
  return null;
};

export const zoomView = (
  [start, end]: TimeRange,
  factor: number,
  anchor: number,
  duration: number,
): TimeRange => {
  const span = clamp(
    (end - start) * factor,
    Math.min(MIN_VIEW_SPAN, duration),
    duration,
  );
  const ratio = end - start > 0 ? (anchor - start) / (end - start) : 0.5;
  const nextStart = clamp(
    anchor - ratio * span,
    0,
    Math.max(0, duration - span),
  );
  return [nextStart, nextStart + span];
};

export const panView = (
  [start, end]: TimeRange,
  delta: number,
  duration: number,
): TimeRange => {
  const span = end - start;
  const nextStart = clamp(start + delta, 0, Math.max(0, duration - span));
  return [nextStart, nextStart + span];
};

export const centerView = (
  [start, end]: TimeRange,
  center: number,
  duration: number,
): TimeRange => panView([start, end], center - (start + end) / 2, duration);

export const fitViewToClip = (clip: TimeRange, duration: number): TimeRange => {
  const padding = Math.max(10, (clip[1] - clip[0]) * 0.6);
  return [
    Math.max(0, clip[0] - padding),
    Math.min(duration, clip[1] + padding),
  ];
};

const TICK_STEPS = [0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800];

export const pickTickStep = (span: number, widthPx: number): number => {
  const target = span / Math.max(1, widthPx / 90);
  return TICK_STEPS.find((step) => step >= target) ?? 3600;
};
